/**
 * Storage telemetry: the event union, and the pre-wiring buffer that stops it
 * being written to nowhere.
 *
 * Storage migrations run at MODULE-EVALUATION time of the atom modules that
 * declare them — they have to, because a `getOnInit` atom reads storage as it
 * is constructed, so a migration that ran any later would seed a key nobody
 * re-reads. App bootstrap
 * (`configureLogger`) is hundreds of modules later in the same eager import
 * graph, and until it runs the logger door is on its safe default with
 * transmission off: every storage event emitted at that point was formatted,
 * handed to the app's handler, logged to a console nobody watches, and
 * dropped before Datadog.
 *
 * So events are BUFFERED (bounded) until app wiring calls
 * `markStorageTelemetryReady()`, then flushed through the same handlers.
 * This mirrors the analytics pre-wiring buffer (`registerAnalyticsAdapter` /
 * `markAnalyticsWiringComplete`) deliberately: the alternative — hoisting the
 * bootstrap import above everything that can reach a storage key — is a
 * property of an 8-hop transitive import chain that no file declares and any
 * import sweep can silently invalidate.
 *
 * The buffer holds the HANDLER with the event, so each consumer's own
 * reporting (levels, redaction, message prefixes) is preserved across the
 * flush.
 */

export type VersionedStorageEvent =
  | { type: 'migrated'; key: string; from: number; to: number }
  | {
      type: 'fallback';
      key: string;
      reason: 'corrupt' | 'migration-failed' | 'future-version' | 'invalid-shape';
    }
  /** A storage write was rejected (quota, private mode, locked-down webview). */
  | { type: 'write-failed'; key: string }
  /** The legacy blob yielded data and the target key was seeded from it. */
  | { type: 'legacy-migrated'; id: string; legacyKey: string; targetKey: string }
  /**
   * The legacy blob was present and readable but produced NOTHING to seed —
   * the target key stays absent and its fallback applies. Distinct from
   * `legacy-migrated` because the two are indistinguishable at the storage
   * layer and opposite for the user: the likeliest real failure is a legacy
   * blob whose slice has already been rewritten away, which would otherwise
   * report as a clean 12-of-12 success with zero keys seeded.
   */
  | { type: 'legacy-skipped'; id: string; legacyKey: string; targetKey: string }
  | { type: 'legacy-migration-failed'; id: string; legacyKey: string };

export type VersionedStorageEventHandler = (event: VersionedStorageEvent) => void;

/**
 * Boot emits a handful of events per key. Twenty production keys plus their
 * cross-key migrations sit an order of magnitude under this; the cap exists so
 * a missing `markStorageTelemetryReady()` leaks a bounded amount of memory and
 * says so, rather than growing without limit.
 */
const BUFFER_LIMIT = 100;

interface BufferedStorageEvent {
  handler: VersionedStorageEventHandler;
  event: VersionedStorageEvent;
}

let telemetryReady = false;
let buffered: BufferedStorageEvent[] = [];
let droppedCount = 0;

function deliver(handler: VersionedStorageEventHandler, event: VersionedStorageEvent) {
  try {
    handler(event);
  } catch (error) {
    // A throwing reporter must not be mistaken for a failed migration: the
    // legacy-migrated emit sits inside the runner's try/catch, so an
    // unguarded throw here would tombstone the run AND report the opposite of
    // what happened.
    // eslint-disable-next-line no-console -- the app logger is not importable here (G1); surface the wiring bug in dev tools
    console.error('[versioned-storage] event handler threw:', error);
  }
}

/**
 * Emit one storage event — or hold it until app wiring is complete. Every
 * event in this module goes through here; consumers cannot opt out and so
 * cannot re-acquire the blindness.
 */
export function emitStorageEvent(
  handler: VersionedStorageEventHandler | undefined,
  event: VersionedStorageEvent
): void {
  if (handler === undefined) return;
  if (telemetryReady) {
    deliver(handler, event);
    return;
  }
  if (buffered.length >= BUFFER_LIMIT) {
    droppedCount += 1;
    // eslint-disable-next-line no-console -- pre-wiring overflow is a wiring bug; surface it loudly in dev tools
    console.warn(
      `[versioned-storage] pre-wiring buffer full (${BUFFER_LIMIT}); dropped ${droppedCount} event(s) — is markStorageTelemetryReady() missing?`
    );
    return;
  }
  buffered.push({ handler, event });
}

/**
 * Called ONCE by app wiring immediately after the logger is configured
 * (src/utils/runtime-wiring.ts). Flushes everything storage emitted during the
 * pre-bootstrap module-evaluation window and stops buffering.
 *
 * Datadog's browser-logs SDK buffers its own pre-`init` calls and drains them
 * on init, so flushing here — before `initDatadog()` runs at `_app` module
 * scope — still reaches the backend.
 */
export function markStorageTelemetryReady(): void {
  telemetryReady = true;
  const toFlush = buffered;
  buffered = [];
  for (const { handler, event } of toFlush) {
    deliver(handler, event);
  }
}

/** Test-only: back to the pre-wiring state. */
export function __resetStorageTelemetryForTests(): void {
  telemetryReady = false;
  buffered = [];
  droppedCount = 0;
}
